import React, { useState, useEffect, useMemo } from "react";
import Icon from "./Icon";
import Modal from "./Modal";
import { StatusBadge, getStatusStyle, getApplicationUnifiedStatus } from "./Badge";
import Skeleton from "./Skeleton";
import DataTable from "./DataTable";
import { EmptyState, ErrorState } from "./State";
import TicketDetailModal from "./TicketDetailModal";
import {
  fetchApplicationTree,
  fetchServers,
} from "../modules/ops/services/CMDBService";
import { fetchTickets } from "../modules/ops/services/TicketsService";

export default function ApplicationDetailModal({
  app,
  onClose,
  onSelectServer,
}) {
  const [activeTab, setActiveTab] = useState("overview");
  const [treeData, setTreeData] = useState([]);
  const [servers, setServers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [serviceSearch, setServiceSearch] = useState("");

  useEffect(() => {
    if (!app?.id) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app?.id]);

  async function loadData() {
    setLoading(true);
    setError(null);
    try {
      const [treeRes, serversRes, ticketsRes] = await Promise.all([
        fetchApplicationTree(),
        fetchServers({ limit: 100, offset: 0 }),
        fetchTickets({ limit: 50, offset: 0 }).catch(() => ({ items: [] })),
      ]);
      setTreeData(treeRes || []);
      setServers(serversRes?.items || []);
      setTickets(ticketsRes?.items || []);
    } catch (err) {
      setError("Failed to load application 360° details.");
    } finally {
      setLoading(false);
    }
  }

  // Find target application node in tree
  const appNode = useMemo(() => {
    if (!treeData.length || !app) return null;
    // Check if it is a root parent node
    const foundParent = treeData.find((p) => p.id === app.id);
    if (foundParent) return { ...foundParent, isParentNode: true };

    // Or check if it is a sub-application
    for (const parent of treeData) {
      const foundSub = (parent.sub_applications || []).find(
        (s) => s.id === app.id,
      );
      if (foundSub) {
        return {
          ...foundSub,
          parentName: parent.name,
          parentCode: parent.code,
          isParentNode: false,
        };
      }
    }
    return null;
  }, [treeData, app]);

  const directServices = useMemo(() => {
    return appNode?.services || [];
  }, [appNode]);

  const subApps = useMemo(() => {
    return appNode?.sub_applications || [];
  }, [appNode]);

  // Aggregate all services across root and sub-applications
  const allServices = useMemo(() => {
    const list = [...directServices];
    subApps.forEach((sub) => {
      (sub.services || []).forEach((svc) => {
        list.push({ ...svc, subAppName: sub.name, subAppCode: sub.code });
      });
    });
    return list;
  }, [directServices, subApps]);

  // Distinct host servers
  const hostServers = useMemo(() => {
    const hostMap = new Map();

    allServices.forEach((svc) => {
      const hName = svc.hostname || "unknown";
      if (!hostMap.has(hName)) {
        // Find matching server record from CMDB servers
        const srvObj = servers.find(
          (s) => s.hostname?.toLowerCase() === hName.toLowerCase(),
        );
        hostMap.set(hName, {
          hostname: hName,
          server: srvObj || null,
          services: [],
        });
      }
      hostMap.get(hName).services.push(svc);
    });

    return Array.from(hostMap.values());
  }, [allServices, servers]);

  // Related tickets
  const relatedTickets = useMemo(() => {
    if (!app?.id) return [];
    return tickets.filter((t) => {
      const matchesApp =
        t.application_id === app.id ||
        (t.application &&
          t.application.toLowerCase().includes(app.name.toLowerCase()));
      const matchesHost = hostServers.some(
        (h) =>
          t.server_hostname?.toLowerCase() === h.hostname.toLowerCase() ||
          t.server_id === h.server?.id,
      );
      return matchesApp || matchesHost;
    });
  }, [tickets, app, hostServers]);

  const rawStatus = getApplicationUnifiedStatus(appNode || app);
  const statusTheme = getStatusStyle(rawStatus);

  const tabs = [
    {
      id: "overview",
      label: "Overview & Fleet",
      icon: "server",
      count: hostServers.length,
    },
    {
      id: "subapps",
      label: "Sub-Applications",
      icon: "layers",
      count: subApps.length,
    },
    {
      id: "services",
      label: "Linked Services",
      icon: "link",
      count: allServices.length,
    },
    {
      id: "tickets",
      label: "Active Tickets",
      icon: "alert",
      count: relatedTickets.length,
    },
  ];

  return (
    <>
      <Modal
        title={`Application 360° — ${app?.name || "Application Details"}`}
        onClose={onClose}
        width={1040}
        bodyStyle={{
          padding: "18px 24px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: 620,
            gap: 14,
          }}
        >
          {/* TOP HEADER HERO */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 18px",
              background: "#F8FAFC",
              borderRadius: 8,
              border: "1px solid #E2E8F0",
              flexShrink: 0,
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: "#DBEAFE",
                  color: "#1D4ED8",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon name="layers" size={22} />
              </div>

              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <h2
                    style={{
                      margin: 0,
                      fontSize: 16,
                      fontWeight: 700,
                      color: "#0F172A",
                    }}
                  >
                    {app?.name}
                  </h2>
                  {app?.code && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        background: "#E2E8F0",
                        color: "#334155",
                        padding: "2px 6px",
                        borderRadius: 4,
                      }}
                    >
                      {app.code}
                    </span>
                  )}
                  {app?.owner_team && (
                    <span
                      style={{
                        fontSize: 11,
                        color: "#475569",
                        background: "#F1F5F9",
                        padding: "2px 8px",
                        borderRadius: 12,
                        border: "1px solid #E2E8F0",
                      }}
                    >
                      {app.owner_team}
                    </span>
                  )}
                  <span
                    style={{
                      fontSize: 10.5,
                      fontWeight: 600,
                      color: app?.is_parent ? "#1E40AF" : "#64748B",
                      background: app?.is_parent ? "#EFF6FF" : "#F8FAFC",
                      border: app?.is_parent
                        ? "1px solid #BFDBFE"
                        : "1px solid #E2E8F0",
                      padding: "2px 6px",
                      borderRadius: 4,
                    }}
                  >
                    {app?.is_parent ? "Root Platform" : "Sub-Application"}
                  </span>
                </div>

                {app?.description && (
                  <p
                    style={{
                      margin: "4px 0 0",
                      fontSize: 12,
                      color: "#64748B",
                    }}
                  >
                    {app.description}
                  </p>
                )}
              </div>
            </div>

            {/* Health Score Pill */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 12px",
                borderRadius: 20,
                background: statusTheme.bg,
                border: `1px solid ${statusTheme.border}`,
              }}
            >
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: statusTheme.dot,
                }}
              />
              <span
                style={{
                  fontSize: 12.5,
                  fontWeight: 700,
                  color: statusTheme.text,
                  textTransform: "capitalize",
                }}
              >
                {rawStatus}
              </span>
            </div>
          </div>

          {/* 4-KPI RIBBON */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 12,
              flexShrink: 0,
            }}
          >
            <div
              style={{
                background: "#FFFFFF",
                borderRadius: 8,
                border: "1px solid #E2E8F0",
                padding: "10px 14px",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 6,
                  background: "#EFF6FF",
                  color: "#2563EB",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon name="server" size={16} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: "#64748B" }}>
                  Host Servers
                </div>
                <div
                  style={{ fontSize: 16, fontWeight: 700, color: "#0F172A" }}
                >
                  {hostServers.length}{" "}
                  <span
                    style={{ fontSize: 11, fontWeight: 400, color: "#64748B" }}
                  >
                    VMs
                  </span>
                </div>
              </div>
            </div>

            <div
              style={{
                background: "#FFFFFF",
                borderRadius: 8,
                border: "1px solid #E2E8F0",
                padding: "10px 14px",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 6,
                  background: "#F0FDF4",
                  color: "#16A34A",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon name="link" size={16} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: "#64748B" }}>
                  Linked Services
                </div>
                <div
                  style={{ fontSize: 16, fontWeight: 700, color: "#0F172A" }}
                >
                  {allServices.length}{" "}
                  <span
                    style={{ fontSize: 11, fontWeight: 400, color: "#64748B" }}
                  >
                    Ports
                  </span>
                </div>
              </div>
            </div>

            <div
              style={{
                background: "#FFFFFF",
                borderRadius: 8,
                border: "1px solid #E2E8F0",
                padding: "10px 14px",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 6,
                  background: "#FAF5FF",
                  color: "#9333EA",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon name="layers" size={16} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: "#64748B" }}>
                  Sub-Applications
                </div>
                <div
                  style={{ fontSize: 16, fontWeight: 700, color: "#0F172A" }}
                >
                  {subApps.length}{" "}
                  <span
                    style={{ fontSize: 11, fontWeight: 400, color: "#64748B" }}
                  >
                    Modules
                  </span>
                </div>
              </div>
            </div>

            <div
              style={{
                background: "#FFFFFF",
                borderRadius: 8,
                border: "1px solid #E2E8F0",
                padding: "10px 14px",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 6,
                  background: relatedTickets.length > 0 ? "#FEE2E2" : "#F8FAFC",
                  color: relatedTickets.length > 0 ? "#DC2626" : "#64748B",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon name="alert" size={16} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: "#64748B" }}>
                  Open Incidents
                </div>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: relatedTickets.length > 0 ? "#DC2626" : "#0F172A",
                  }}
                >
                  {relatedTickets.length}{" "}
                  <span
                    style={{ fontSize: 11, fontWeight: 400, color: "#64748B" }}
                  >
                    Tickets
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* TAB NAVIGATION BAR */}
          <div
            style={{
              display: "flex",
              gap: 8,
              borderBottom: "1px solid #E2E8F0",
              paddingBottom: 2,
              flexShrink: 0,
            }}
          >
            {tabs.map((t) => {
              const isActive = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setActiveTab(t.id)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "7px 14px",
                    borderRadius: "6px 6px 0 0",
                    border: "none",
                    background: "transparent",
                    borderBottom: isActive
                      ? "2px solid #2563EB"
                      : "2px solid transparent",
                    color: isActive ? "#2563EB" : "#64748B",
                    fontWeight: isActive ? 600 : 500,
                    fontSize: 12.5,
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                >
                  <Icon
                    name={t.icon}
                    size={14}
                    color={isActive ? "#2563EB" : "#64748B"}
                  />
                  <span>{t.label}</span>
                  {t.count !== undefined && (
                    <span
                      style={{
                        fontSize: 11,
                        padding: "1px 6px",
                        borderRadius: 10,
                        background: isActive ? "#DBEAFE" : "#F1F5F9",
                        color: isActive ? "#1D4ED8" : "#64748B",
                        fontWeight: 600,
                      }}
                    >
                      {t.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* SCROLLABLE TAB CONTENT BODY */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              paddingRight: 4,
            }}
          >
            {loading ? (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 10 }}
              >
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} height={60} style={{ borderRadius: 6 }} />
                ))}
              </div>
            ) : error ? (
              <ErrorState message={error} onRetry={loadData} />
            ) : activeTab === "overview" ? (
              /* TAB 1: OVERVIEW & HOST FLEET */
              <div
                style={{ display: "flex", flexDirection: "column", gap: 14 }}
              >
                <div
                  style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}
                >
                  Host Infrastructure Fleet ({hostServers.length} Servers)
                </div>

                <DataTable
                  columns={[
                    {
                      header: "Host Server",
                      key: "hostname",
                      tip: "Host virtual machine or physical server name",
                      render: (val) => (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            fontWeight: 600,
                            color: "#0F172A",
                          }}
                        >
                          <Icon name="server" size={13} color="#2563EB" />
                          <span>{val}</span>
                        </div>
                      ),
                    },
                    {
                      header: "IP Address",
                      key: "server",
                      tip: "Primary IP address",
                      render: (srv) => (
                        <span
                          style={{ fontFamily: "monospace", color: "#475569" }}
                        >
                          {srv?.specs?.ip_address || "—"}
                        </span>
                      ),
                    },
                    {
                      header: "OS / Platform",
                      key: "server",
                      tip: "Operating system distribution",
                      render: (srv) => (
                        <span style={{ color: "#64748B" }}>
                          {srv?.os || "Linux"}
                        </span>
                      ),
                    },
                    {
                      header: "Status",
                      key: "server",
                      tip: "Server runtime status",
                      render: (srv) => <StatusBadge entity={srv} type="server" />,
                    },
                    {
                      header: "Bound Ports",
                      key: "services",
                      tip: "Associated port services",
                      render: (srvList) => (
                        <div
                          style={{ display: "flex", flexWrap: "wrap", gap: 4 }}
                        >
                          {(srvList || []).map((s, idx) => {
                            const port =
                              s.metadata?.port ||
                              (s.service_name.includes(":")
                                ? s.service_name.split(":")[1]
                                : null);
                            return (
                              <span
                                key={idx}
                                style={{
                                  fontSize: 10.5,
                                  fontWeight: 600,
                                  padding: "1px 5px",
                                  borderRadius: 4,
                                  background: "#EFF6FF",
                                  color: "#1D4ED8",
                                  border: "1px solid #BFDBFE",
                                }}
                              >
                                {s.service_name} {port ? `(:${port})` : ""}
                              </span>
                            );
                          })}
                        </div>
                      ),
                    },
                    {
                      header: "Action",
                      key: "server",
                      align: "right",
                      render: (srv) =>
                        srv && onSelectServer ? (
                          <button
                            type="button"
                            onClick={() => onSelectServer(srv)}
                            style={{
                              padding: "3px 8px",
                              borderRadius: 4,
                              border: "1px solid #CBD5E1",
                              background: "#FFFFFF",
                              color: "#2563EB",
                              fontSize: 11,
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                          >
                            View VM
                          </button>
                        ) : null,
                    },
                  ]}
                  data={hostServers}
                  keyField="hostname"
                  emptyTitle="No host servers linked"
                  emptySubtitle="This application currently has no port services linked to host VMs."
                  emptyIcon="server"
                />
              </div>
            ) : activeTab === "subapps" ? (
              /* TAB 2: SUB-APPLICATIONS & MICROSERVICES */
              <div
                style={{ display: "flex", flexDirection: "column", gap: 14 }}
              >
                <div
                  style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}
                >
                  Sub-Applications & Microservices ({subApps.length})
                </div>

                {subApps.length === 0 ? (
                  <EmptyState
                    title="No sub-applications registered"
                    subtitle="This application operates as a standalone service with no nested sub-applications."
                  />
                ) : (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(300px, 1fr))",
                      gap: 12,
                    }}
                  >
                    {subApps.map((sub) => {
                      const svcs = sub.services || [];
                      return (
                        <div
                          key={sub.id}
                          style={{
                            background: "#FFFFFF",
                            borderRadius: 6,
                            border: "1px solid #E2E8F0",
                            padding: "12px 14px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            gap: 10,
                          }}
                        >
                          <div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                }}
                              >
                                <span
                                  style={{
                                    fontWeight: 600,
                                    fontSize: 13,
                                    color: "#0F172A",
                                  }}
                                >
                                  {sub.name}
                                </span>
                                {sub.code && (
                                  <span
                                    style={{
                                      fontSize: 10,
                                      fontWeight: 600,
                                      background: "#F1F5F9",
                                      color: "#475569",
                                      padding: "1px 5px",
                                      borderRadius: 3,
                                    }}
                                  >
                                    {sub.code}
                                  </span>
                                )}
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  flexShrink: 0,
                                }}
                              >
                                <StatusBadge
                                  status={getApplicationUnifiedStatus(sub)}
                                />
                              </div>
                            </div>

                            {sub.owner_team && (
                              <div
                                style={{
                                  fontSize: 11,
                                  color: "#64748B",
                                  marginTop: 2,
                                }}
                              >
                                Team: {sub.owner_team}
                              </div>
                            )}

                            {sub.description && (
                              <p
                                style={{
                                  margin: "6px 0 0",
                                  fontSize: 11.5,
                                  color: "#475569",
                                }}
                              >
                                {sub.description}
                              </p>
                            )}
                          </div>

                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 4,
                              borderTop: "1px solid #F1F5F9",
                              paddingTop: 8,
                            }}
                          >
                            {svcs.length === 0 ? (
                              <span
                                style={{
                                  fontSize: 11,
                                  color: "#94A3B8",
                                  fontStyle: "italic",
                                }}
                              >
                                No services linked
                              </span>
                            ) : (
                              svcs.map((s, idx) => (
                                <span
                                  key={idx}
                                  style={{
                                    fontSize: 10,
                                    padding: "1px 6px",
                                    borderRadius: 4,
                                    background: "#F8FAFC",
                                    border: "1px solid #CBD5E1",
                                    color: "#334155",
                                  }}
                                >
                                  {s.hostname}: {s.service_name}
                                </span>
                              ))
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : activeTab === "services" ? (
              /* TAB 3: LINKED PORT SERVICES */
              <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}
                  >
                    All Linked Port Services ({allServices.length})
                  </div>
                  {allServices.length > 5 && (
                    <input
                      type="text"
                      placeholder="Filter services or port..."
                      value={serviceSearch}
                      onChange={(e) => setServiceSearch(e.target.value)}
                      style={{
                        padding: "4px 8px",
                        borderRadius: 5,
                        border: "1px solid #CBD5E1",
                        fontSize: 11.5,
                        width: 180,
                      }}
                    />
                  )}
                </div>

                {allServices.length === 0 ? (
                  <EmptyState
                    title="No port services linked"
                    subtitle="No host port services are mapped to this application."
                  />
                ) : (
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 8 }}
                  >
                    {allServices
                      .filter((s) => {
                        if (!serviceSearch) return true;
                        const term = serviceSearch.toLowerCase();
                        return (
                          s.service_name?.toLowerCase().includes(term) ||
                          s.hostname?.toLowerCase().includes(term) ||
                          (s.subAppName &&
                            s.subAppName.toLowerCase().includes(term))
                        );
                      })
                      .map((s, idx) => {
                        const port =
                          s.metadata?.port ||
                          (s.service_name.includes(":")
                            ? s.service_name.split(":")[1]
                            : null);

                        return (
                          <div
                            key={idx}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "9px 12px",
                              borderRadius: 6,
                              border: "1px solid #E2E8F0",
                              background: "#FFFFFF",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                              }}
                            >
                              <Icon name="server" size={14} color="#2563EB" />
                              <div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      fontSize: 13,
                                      color: "#0F172A",
                                    }}
                                  >
                                    {s.service_name}
                                  </span>
                                  {port && (
                                    <span
                                      style={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        color: "#0284C7",
                                        background: "#E0F2FE",
                                        padding: "1px 6px",
                                        borderRadius: 4,
                                        fontFamily: "monospace",
                                      }}
                                    >
                                      :{port}
                                    </span>
                                  )}
                                  <span
                                    style={{
                                      fontSize: 10.5,
                                      padding: "1px 6px",
                                      borderRadius: 4,
                                      background: "#F1F5F9",
                                      color: "#475569",
                                    }}
                                  >
                                    {s.relation_type || "owner"}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: "#64748B",
                                    marginTop: 2,
                                  }}
                                >
                                  Host:{" "}
                                  <strong style={{ color: "#334155" }}>
                                    {s.hostname}
                                  </strong>
                                  {s.subAppName &&
                                    ` • Linked to sub-app: ${s.subAppName}`}
                                </div>
                              </div>
                            </div>

                            <StatusBadge entity={s} type="service" />
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            ) : (
              /* TAB 4: ACTIVE TICKETS & INCIDENTS */
              <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                <div
                  style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}
                >
                  Active Incidents & Operations Tickets ({relatedTickets.length}
                  )
                </div>

                {relatedTickets.length === 0 ? (
                  <EmptyState
                    title="No active incidents"
                    subtitle="This application has zero open tickets or active alarms."
                  />
                ) : (
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 8 }}
                  >
                    {relatedTickets.map((t) => (
                      <div
                        key={t.id}
                        onClick={() => setSelectedTicket(t)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "10px 14px",
                          borderRadius: 6,
                          border: "1px solid #FECACA",
                          background: "#FFF5F5",
                          cursor: "pointer",
                          transition: "all 0.15s ease",
                        }}
                      >
                        <div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                background: "#DC2626",
                                color: "#FFFFFF",
                                padding: "1px 6px",
                                borderRadius: 4,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {t.ticket_key || t.id}
                            </span>
                            <span
                              style={{
                                fontWeight: 600,
                                fontSize: 13,
                                color: "#0F172A",
                              }}
                            >
                              {t.title}
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              color: "#64748B",
                              marginTop: 4,
                            }}
                          >
                            Priority:{" "}
                            <strong style={{ color: "#DC2626" }}>
                              {t.priority || "High"}
                            </strong>{" "}
                            • Status:{" "}
                            <strong style={{ color: "#334155" }}>
                              {t.status}
                            </strong>{" "}
                            • Created:{" "}
                            {new Date(
                              t.created_at || Date.now(),
                            ).toLocaleDateString()}
                          </div>
                        </div>

                        <button
                          type="button"
                          style={{
                            padding: "4px 10px",
                            borderRadius: 4,
                            border: "1px solid #CBD5E1",
                            background: "#FFFFFF",
                            color: "#1E293B",
                            fontSize: 11.5,
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          View Ticket
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* MODAL FOOTER */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              paddingTop: 10,
              borderTop: "1px solid #E2E8F0",
              flexShrink: 0,
            }}
          >
            <div
              style={{ fontSize: 11.5, color: "#64748B", whiteSpace: "nowrap" }}
            >
              Application ID:{" "}
              <span
                style={{
                  fontFamily: "monospace",
                  color: "#1E293B",
                  fontWeight: 600,
                }}
              >
                {app?.id}
              </span>
            </div>
          </div>
        </div>
      </Modal>

      {selectedTicket && (
        <TicketDetailModal
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
        />
      )}
    </>
  );
}
